/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDispatch } from "react-redux";
import React, { useState } from "react";
import { Typography, Box, Button } from "@mui/material";

import { CtcCreateEdit } from "./CtcCreateEdit";
import { CtcInfoPanel } from "./CtcInfoPanel";
import List from "../../../../../components/List";
import { EmptyView } from "../../../../../components/OList";
import { rupeeDisplay } from "../../../../../helpers/rupee";
import { ICtc } from "../../../../../interfaces/CtcInterface";
import { useCtcs } from "../../../../../hooks/query/useCtcs";
import { Employee, UserMode } from "../../../../../interfaces/User";
import { updateEmployeeWithCtc } from "../../../../../actions/employee";
import { updateServiceProviderWithCtc } from "../../../../../actions/serviceProvider";
import { ServiceProvider } from "../../../../../interfaces/ServiceProvidersInterface";
import EmployeeAndSpSelect from "../../../../../components/Employee/EmployeeAndSpSelect";
import { bsFullDate } from "../../../../../components/Calendar/functions/calendarFunctions";

// eslint-disable-next-line import/prefer-default-export
export function CtcList(): React.JSX.Element {
  const dispatch = useDispatch();

  const [selectedCtc, setSelectedCtc] = useState<ICtc | null>(null);
  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(null);

  const [filter, setFilter] = useState<{
    employeeIds: number[];
    serviceProviderIds: number[];
  }>({
    employeeIds: [],
    serviceProviderIds: []
  });

  const { data: ctcData = [], isLoading } = useCtcs(filter);

  const handleSuccess = () => {
    setSelectedCtc(null);
    setShowCreateEditPanel(false);
  };

  return (
    <Box
      sx={{
        height: "100%"
      }}
      data-testmation="ctcList"
    >
      <List<ICtc>
        automation="ctcList"
        data={ctcData}
        isLoading={isLoading}
        withoutSearch
        isResponsive
        hideCreateButton
        customCreateButton={
          <Button
            variant="contained"
            data-testmation="createCtcButton"
            sx={{ height: 35 }}
            onClick={() => {
              setShowCreateEditPanel(true);
              setSelectedCtc(null);
            }}
          >
            Create CTC
          </Button>
        }
        additionalHeaderFilters={
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center"
            }}
          >
            <EmployeeAndSpSelect
              selectedEmployee={selectedEmployee}
              onChange={(emp) => {
                setSelectedEmployee(emp);
                setFilter((prev) => {
                  if (!emp) {
                    return {
                      ...prev,
                      employeeIds: [],
                      serviceProviderIds: []
                    };
                  }

                  if (emp?.userAuthenticable[0]?.authenticable === UserMode.Employee) {
                    return {
                      ...prev,
                      employeeIds: emp ? [emp.id] : [],
                      serviceProviderIds: []
                    };
                  }

                  if (emp?.userAuthenticable[0]?.authenticable === UserMode.ServiceProvider) {
                    return {
                      ...prev,
                      employeeIds: [],
                      serviceProviderIds: emp ? [emp.id] : []
                    };
                  }

                  return prev;
                });
              }}
              sx={{ height: 35, width: 200 }}
            />
          </Box>
        }
        activeRow={selectedCtc && selectedCtc.id}
        onRowClick={(ctc) => {
          const unselect = selectedCtc && selectedCtc.id === ctc.id;

          if (unselect) {
            setSelectedCtc(null);
          } else {
            setSelectedCtc(ctc as ICtc);
          }
        }}
        columns={[
          {
            key: "effectiveFrom",
            label: "Effective From",
            sortable: true,
            formatter: ({ effectiveFrom }) => (
              <Typography component="span" variant="body2">
                {bsFullDate(effectiveFrom)}
              </Typography>
            )
          },
          {
            key: "name",
            label: "Name",
            sortable: true,
            formatter: (row) => {
              let name = "-";
              if (row.employee) {
                name = `${row.employee.firstName} ${row.employee.lastName}`;
              } else if (row.serviceProvider) {
                name = `${row.serviceProvider.firstName} ${row.serviceProvider.lastName}`;
              }
              return (
                <Typography component="span" variant="body2" sx={{ textTransform: "capitalize" }}>
                  {name}
                </Typography>
              );
            }
          },
          {
            key: "type",
            label: "Employee Type",
            sortable: true,
            formatter: (row) => (
              <Typography component="span" variant="body2">
                {row.employeeId ? "Employee" : "Service Provider"}
              </Typography>
            )
          },
          {
            key: "basicSalary",
            label: "Basic Salary",
            sortable: true,
            formatter: ({ monthlyEarnings }) => (
              <Typography component="span" variant="body2">
                {rupeeDisplay(monthlyEarnings?.basicSalary || 0)}
              </Typography>
            )
          }
        ]}
      >
        <EmptyView>
          <Typography component="div" variant="body2">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                width: "100%"
              }}
            >
              <Typography component="div">
                <Box textAlign="center" marginTop="16px">
                  No ctc records found
                </Box>
              </Typography>
            </Box>
          </Typography>
        </EmptyView>
      </List>

      {selectedCtc && (
        <CtcInfoPanel
          ctc={selectedCtc}
          onClose={() => setSelectedCtc(null)}
          onEditOpen={() => setShowCreateEditPanel(true)}
          onDeleteSuccess={(deletedCtc) => {
            setSelectedCtc(null);

            if (deletedCtc?.employeeId) {
              dispatch(updateEmployeeWithCtc(deletedCtc.employeeId, null));
            } else if (deletedCtc?.serviceProviderId) {
              dispatch(updateServiceProviderWithCtc(deletedCtc?.serviceProviderId, null));
            }
          }}
        />
      )}

      {showCreateEditPanel && (
        <CtcCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedCtc(null);
          }}
          ctc={selectedCtc}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
